import { parseAsString, useQueryState } from 'nuqs';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const BLOCKER_PREFIX_PATTERN = /^(?:blocker|b):/;

export const resolveEditingValue = (isBlocker: boolean, appointmentId: string) => {
  if (isBlocker) {
    return `blocker:${appointmentId}`;
  }

  return appointmentId;
};

export const parseEditingValue = (value: string | null) => {
  if (!value) {
    return { id: null, isBlocker: false } as const;
  }

  if (BLOCKER_PREFIX_PATTERN.test(value)) {
    return { id: value.replace(/^(?:blocker|b):/, ''), isBlocker: true } as const;
  }

  return { id: value, isBlocker: false } as const;
};

/** Resolve the semantic intent of the current isCreating URL value. */
export const resolveCreatingMode = (
  isCreating: string | null,
):
  | { mode: 'none' }
  | { mode: 'blank' }
  | { date: string; mode: 'date' }
  | { mode: 'workOrder'; workOrderId: number } => {
  if (!isCreating) {
    return { mode: 'none' };
  }

  if (isCreating === 'true') {
    return { mode: 'blank' };
  }

  if (DATE_PATTERN.test(isCreating)) {
    return { mode: 'date', date: isCreating };
  }

  const asInt = Number.parseInt(isCreating, 10);

  if (!Number.isNaN(asInt)) {
    return { mode: 'workOrder', workOrderId: asInt };
  }

  return { mode: 'blank' };
};

export const useAppointmentQueryState = () => {
  const [isCreating, setIsCreating] = useQueryState('isCreating', parseAsString);
  const [isEditing, setIsEditing] = useQueryState('isEditing', parseAsString);

  /** Open create drawer with no prefill. */
  const openForCreating = () => {
    setIsEditing(null);
    setIsCreating('true');
  };

  /** Open create drawer with date prefilled (yyyy-MM-dd). */
  const openForCreatingAtDate = (dateString: string) => {
    setIsEditing(null);
    setIsCreating(dateString);
  };

  /** Open create drawer with a work order prefilled. */
  const openForCreatingByWorkOrder = (workOrderId: number) => {
    setIsEditing(null);
    setIsCreating(workOrderId.toString());
  };

  const openForEditing = (appointmentId: string, isBlocker = false) => {
    setIsCreating(null);
    setIsEditing(resolveEditingValue(isBlocker, appointmentId));
  };

  const close = () => {
    setIsCreating(null);
    setIsEditing(null);
  };

  return {
    close,
    isCreating,
    isEditing,
    openForCreating,
    openForCreatingAtDate,
    openForCreatingByWorkOrder,
    openForEditing,
  };
};
